import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";

export default function EntityDialog({ open, onOpenChange, fields, initial, title, endpoint, method, onSaved, optionSources = {}, duplicateCheck }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState([]);

  useEffect(() => {
    if (open) {
      const base = {};
      fields.forEach((f) => {
        let v = initial?.[f.name];
        if (f.type === "tags" && Array.isArray(v)) v = v.join(", ");
        base[f.name] = v ?? (f.type === "switch" ? false : "");
      });
      setForm(base);
    }
  }, [open, initial, fields]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (force = false) => {
    for (const f of fields) {
      if (f.required && !form[f.name]) { toast.error(`${f.label} is required`); return; }
    }
    if (duplicateCheck && method !== "put" && !force) {
      try {
        const { data } = await api.get(`${duplicateCheck}?name=${encodeURIComponent(form.business_name || form.name || "")}&phone=${encodeURIComponent(form.phone || "")}&email=${encodeURIComponent(form.email || "")}`);
        if (data.matches && data.matches.length) { setDupes(data.matches); return; }
      } catch { /* non-blocking */ }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number" && payload[f.name] !== "" && payload[f.name] != null) payload[f.name] = Number(payload[f.name]);
        if (f.type === "tags") payload[f.name] = String(payload[f.name] || "").split(",").map((s) => s.trim()).filter(Boolean);
      });
      if (method === "put") await api.put(endpoint, payload);
      else await api.post(endpoint, payload);
      toast.success("Saved");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resolveOptions = (f) => {
    if (f.optionsSource) {
      const opts = optionSources[f.optionsSource] || [];
      const cur = form?.[f.name];
      if (cur && typeof cur === "string" && !opts.some((o) => o.value === cur)) {
        const label = f.name === "business_id" ? (form.business_name || cur) : f.name === "zone_id" ? [form.territory, form.zone].filter(Boolean).join(" › ") || cur : cur;
        return [{ value: cur, label }, ...opts];
      }
      return opts;
    }
    return (f.options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="entity-dialog">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        {dupes.length > 0 && (
          <div className="rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3 text-sm" data-testid="dup-warning">
            <p className="font-semibold text-[#8a5a00]">This business may already exist:</p>
            <ul className="mt-1 space-y-0.5 text-[#8a5a00]">
              {dupes.map((m) => <li key={m.id}>• {m.label}{m.sub ? ` — ${m.sub}` : ""} <span className="opacity-60">({m.type})</span></li>)}
            </ul>
            <p className="mt-1 text-xs text-slate-500">Review above, or create anyway if this is a different business.</p>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className={f.full || f.type === "textarea" ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-medium text-slate-600">{f.label}{f.required && <span className="text-[#FE3967]"> *</span>}</Label>
              {f.type === "textarea" ? (
                <Textarea className="mt-1" value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
              ) : f.type === "select" ? (
                <Select value={form[f.name] ? String(form[f.name]) : ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger className="mt-1" data-testid={`field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {resolveOptions(f).map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : f.type === "switch" ? (
                <div className="mt-2"><Switch checked={!!form[f.name]} onCheckedChange={(v) => set(f.name, v)} data-testid={`field-${f.name}`} /></div>
              ) : (
                <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "time" ? "time" : "text"}
                  className="mt-1" value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="dialog-cancel">Cancel</Button>
          {dupes.length > 0
            ? <Button onClick={() => submit(true)} disabled={saving} className="bg-[#F59E0B] hover:bg-[#d97f06]" data-testid="dialog-create-anyway">{saving ? "Saving…" : "Create Anyway"}</Button>
            : <Button onClick={() => submit(false)} disabled={saving} data-testid="dialog-save">{saving ? "Saving…" : "Save"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
